import React from 'react';

/**
 * Fases emitidas por el backend en `yupana-solicitud-progreso`.
 * El orden aquí define el paso visual del stepper.
 */
const FASES = [
    { key: 'INICIANDO',             pct: 10,  label: 'Iniciando transacción',          icon: '🚀' },
    { key: 'SOLICITUD_GUARDADA',    pct: 30,  label: 'Solicitud guardada',        icon: '💾' },
    { key: 'RUBROS_GUARDADOS',      pct: 55,  label: 'Rubros / ingresos guardados',     icon: '💰' },
    { key: 'REFERENCIAS_GUARDADAS', pct: 75,  label: 'Referencias personales guardadas',icon: '👥' },
    { key: 'DIRECCIONES_GUARDADAS', pct: 90,  label: 'Direcciones guardadas',           icon: '📍' },
    { key: 'COMPLETADO',            pct: 100, label: 'Transacción confirmada',    icon: '✅' },
];

/**
 * Máquina de estados del componente:
 *
 *   LOADING             — recibiendo eventos `yupana-solicitud-progreso`
 *   PROCESSING_EXTERNAL — fase === 'COMPLETADO', esperando `solicitud-web-changed`
 *                         (BD committed, APIs externas en proceso)
 *   SUCCESS             — recibido `solicitud-web-changed` con origen === 'yupana'
 *   ERROR               — fase === 'ERROR', rollback en BD
 *
 * Props:
 *   isOpen   boolean
 *   fase     string   — clave de FASES o 'ERROR'
 *   progreso number   — 0-100
 *   mensaje  string   — texto del backend para el usuario
 *   estado   string   — 'LOADING' | 'PROCESSING_EXTERNAL' | 'SUCCESS' | 'ERROR'
 *   totales  object?  — { rubros, referencias, direcciones }  (presente en COMPLETADO)
 */
const fmt = (n) =>
    new Intl.NumberFormat('es-EC', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(n ?? 0);

// ── Cuadro de resultado de cupo ──────────────────────────────────────────────
// cupEstado: 'Aprobada' | 'Rechazada' | 'Espera Aprobación' | 'Caducado' | 'Inexistente'
function CupoResultado({ cupoInfo }) {
    const { estado, disponible } = cupoInfo;

    if (estado === 'Aprobada') {
        return (
            <div className="bg-green-50 border-2 border-green-400 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center flex-shrink-0">
                        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l3-3z" clipRule="evenodd"/>
                        </svg>
                    </div>
                    <div>
                        <p className="text-xs font-bold text-green-600 uppercase tracking-wider">Crédito Aprobado</p>
                        <p className="text-sm font-semibold text-green-800">¡Tienes cupo disponible!</p>
                    </div>
                    <span className="ml-auto bg-green-200 text-green-800 text-xs font-bold px-2.5 py-1 rounded-full">
                        {estado}
                    </span>
                </div>
                <div className="bg-white border border-green-200 rounded-lg px-4 py-3 text-center">
                    <p className="text-xs text-slate-500 mb-0.5">Cupo Disponible</p>
                    <p className="text-3xl font-black text-green-600 tracking-tight">{fmt(disponible)}</p>
                </div>
            </div>
        );
    }

    if (estado === 'Rechazada') {
        return (
            <div className="bg-red-50 border-2 border-red-400 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-red-500 rounded-xl flex items-center justify-center flex-shrink-0">
                        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
                        </svg>
                    </div>
                    <div>
                        <p className="text-xs font-bold text-red-600 uppercase tracking-wider">Crédito No Aprobado</p>
                        <p className="text-sm font-semibold text-red-800">Por el momento no aplica</p>
                    </div>
                    <span className="ml-auto bg-red-200 text-red-800 text-xs font-bold px-2.5 py-1 rounded-full">
                        {estado}
                    </span>
                </div>
                <p className="text-xs text-red-700 leading-relaxed bg-red-100 rounded-lg px-3 py-2.5">
                    El cliente no califica para crédito en este momento. Puedes intentarlo nuevamente
                    en una próxima ocasión o consultar con el área de crédito.
                </p>
            </div>
        );
    }

    // Espera Aprobación | Caducado | Inexistente → estado pendiente/revisión
    return (
        <div className="bg-amber-50 border-2 border-amber-400 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-amber-400 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd"/>
                    </svg>
                </div>
                <div>
                    <p className="text-xs font-bold text-amber-700 uppercase tracking-wider">En Revisión</p>
                    <p className="text-sm font-semibold text-amber-900">Solicitud enviada para evaluación</p>
                </div>
                <span className="ml-auto bg-amber-200 text-amber-800 text-xs font-bold px-2.5 py-1 rounded-full whitespace-nowrap">
                    {estado}
                </span>
            </div>
            <p className="text-xs text-amber-800 leading-relaxed bg-amber-100 rounded-lg px-3 py-2.5">
                Su solicitud fue enviada para revisión. Espere confirmación del área de crédito.
            </p>
        </div>
    );
}

export function ProgresoYupana({
    isOpen,
    fase        = '',
    progreso    = 0,
    mensaje     = '',
    estado      = 'LOADING',
    totales     = null,
    cupoInfo    = null,  // { estado: string, disponible: number }
}) {
    if (!isOpen) return null;

    const isError   = estado === 'ERROR';
    const isSuccess = estado === 'SUCCESS';
    const isExternal= estado === 'PROCESSING_EXTERNAL';
    const isLoading = estado === 'LOADING';

    // ── Barra de progreso ────────────────────────────────────────────────────
    const barColor = isError    ? 'from-red-500 to-red-600'
                   : isSuccess  ? 'from-green-500 to-emerald-500'
                   :              'from-blue-500 to-blue-600';

    // Pulsar mientras haya actividad en curso
    const barPulse = (isLoading || isExternal) && !isError;

    // ── Icono + título ───────────────────────────────────────────────────────
    const headerMeta = isError    ? { icon: '❌', title: 'Error en el proceso' }
                     : isSuccess  ? { icon: '🎉', title: '¡Solicitud creada exitosamente!' }
                     : isExternal ? { icon: '⚡', title: 'Procesando con Yupana…' }
                     :              { icon: '⚙️', title: 'Guardando solicitud' };

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label="Progreso de solicitud Yupana"
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        >
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-8 space-y-6">

                {/* ── Icono + título ───────────────────────────────────── */}
                <div className="text-center">
                    <div className="text-5xl mb-2" role="img" aria-hidden="true">
                        {headerMeta.icon}
                    </div>
                    <h2 className="text-xl font-bold text-slate-800">{headerMeta.title}</h2>
                    <p className="text-sm text-slate-500 mt-1 leading-relaxed">
                        {isExternal
                            ? 'Los datos se guardaron correctamente. Yupana está consultando tu cupo de crédito…'
                            : isSuccess
                            ? (mensaje || '¡Todo listo! Redirigiendo…')
                            : (mensaje || 'Procesando…')}
                    </p>
                </div>

                {/* ── Barra de progreso ────────────────────────────────── */}
                {!isError && (
                    <div>
                        <div className="flex justify-between text-sm mb-1.5">
                            <span className="font-medium text-slate-600 truncate max-w-[70%]">
                                {fase?.replace(/_/g, ' ')}
                            </span>
                            <span className={`font-bold tabular-nums ${isSuccess ? 'text-green-600' : 'text-blue-600'}`}>
                                {progreso}%
                            </span>
                        </div>
                        <div
                            className="w-full bg-slate-100 rounded-full h-3 overflow-hidden"
                            role="progressbar"
                            aria-valuenow={progreso}
                            aria-valuemin={0}
                            aria-valuemax={100}
                        >
                            <div
                                className={`h-full rounded-full bg-gradient-to-r transition-all duration-700 ease-out
                                    ${barColor} ${barPulse ? 'animate-pulse' : ''}`}
                                style={{ width: `${Math.max(progreso, 2)}%` }}
                            />
                        </div>
                    </div>
                )}

                {/* ── Stepper de fases ─────────────────────────────────── */}
                {!isSuccess && !isError && (
                    <div className="space-y-2.5">
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                            Etapas del proceso
                        </p>
                        {FASES.map((step) => {
                            const done   = progreso >= step.pct;
                            const active = fase === step.key && !isSuccess;
                            return (
                                <div key={step.key} className="flex items-center gap-3">
                                    {/* Indicador circular */}
                                    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0
                                        text-xs font-bold transition-all duration-300
                                        ${done
                                            ? 'bg-green-500 text-white shadow-sm shadow-green-200'
                                            : active
                                            ? 'bg-blue-500 text-white shadow-sm shadow-blue-200 scale-110'
                                            : 'bg-slate-100 text-slate-400'
                                        }`}
                                    >
                                        {done
                                            ? <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                                              </svg>
                                            : <span>{step.icon}</span>
                                        }
                                    </div>

                                    {/* Label */}
                                    <span className={`text-sm flex-1 transition-colors
                                        ${done || active ? 'text-slate-800 font-medium' : 'text-slate-400'}`}>
                                        {step.label}
                                    </span>

                                    {/* Badge de porcentaje en el paso activo */}
                                    {active && (
                                        <span className="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                                            {step.pct}%
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* ── Aviso: APIs externas en proceso ─────────────────── */}
                {isExternal && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                        <div className="flex items-start gap-3">
                            <svg className="animate-spin h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                            </svg>
                            <div>
                                <p className="text-sm font-semibold text-amber-800">Procesando</p>
                                <p className="text-xs text-amber-700 mt-0.5 leading-relaxed">
                                    Consulta de cupo · Solicitud de crédito.
                                    No cierres esta ventana.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* ── Éxito: resultado de cupo + totales ──────────────── */}
                {isSuccess && (
                    <div className="space-y-3">

                        {/* Confirmación de flujo */}
                        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
                            <svg className="h-5 w-5 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l3-3z" clipRule="evenodd"/>
                            </svg>
                            <p className="text-sm font-semibold text-green-800">
                                Flujo completado — cupo · crédito
                            </p>
                        </div>

                        {/* ── Cuadro de resultado de cupo ─────────────────── */}
                        {cupoInfo && <CupoResultado cupoInfo={cupoInfo} />}

                        {/* Contadores de registros guardados */}
                        {totales && (
                            <div className="grid grid-cols-3 gap-3">
                                {[
                                    { key: 'rubros',      label: 'Rubros',      icon: '💰' },
                                    { key: 'referencias', label: 'Referencias', icon: '👥' },
                                    { key: 'direcciones', label: 'Direcciones', icon: '📍' },
                                ].map(({ key, label, icon }) => (
                                    <div key={key} className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-center">
                                        <p className="text-base mb-0.5">{icon}</p>
                                        <p className="text-2xl font-black text-slate-800">{totales[key] ?? 0}</p>
                                        <p className="text-xs text-slate-500">{label}</p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* ── Error: detalle del rollback ──────────────────────── */}
                {isError && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                        <svg className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                        </svg>
                        <div>
                            <p className="text-sm font-semibold text-red-800">La transacción fue revertida</p>
                            <p className="text-xs text-red-700 mt-0.5 leading-relaxed">
                                {mensaje || 'Ocurrió un error. Puedes intentarlo nuevamente.'}
                            </p>
                        </div>
                    </div>
                )}

                {/* ── Spinner de actividad (LOADING) ───────────────────── */}
                {isLoading && (
                    <div className="flex justify-center">
                        <svg className="animate-spin h-7 w-7 text-blue-600" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                        </svg>
                    </div>
                )}
            </div>
        </div>
    );
}
